import { timeZoneDateYmd } from "./date-utils";
import {
  normalizePeopleStatuses,
  statusMatchesCategory,
  type PeopleStatusOption,
} from "./people-status";

export type PeopleRoleCounts = {
  staff: number;
  volunteers: number;
};

export type PeopleFilterKey =
  | "all"
  | "recent"
  | "new"
  | "staff"
  | "volunteers"
  | "members"
  | "visitors"
  | "donors"
  | "vendors"
  | "prospects";

export type PeopleFilterCounts = Record<PeopleFilterKey, number>;

export type PeopleFilterOptions = {
  activeFilter: PeopleFilterKey;
  searchQuery?: string;
  statusOptions?: PeopleStatusOption[];
  /** The moment "this month" is measured from. Defaults to now; tests pin it. */
  now?: Date;
  /** The organization's IANA time zone; "this month" follows its calendar. */
  timeZone?: string | null;
};

const monthFormatters = new Map<string, Intl.DateTimeFormat>();

/** `YYYY-MM` of `date` on the organization's calendar (browser local time when no zone is given). */
function monthKey(date: Date, timeZone?: string | null): string {
  if (!timeZone) return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
  let formatter = monthFormatters.get(timeZone);
  if (!formatter) {
    try {
      formatter = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit" });
    } catch {
      // An unrecognised zone name falls back to the same default the rest of the app uses.
      return timeZoneDateYmd(timeZone, date).slice(0, 7);
    }
    monthFormatters.set(timeZone, formatter);
  }
  const parts = formatter.formatToParts(date);
  const value = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return `${value("year")}-${value("month")}`;
}

// Filtering and counting call the predicate once per person with the same `now`, so the current
// month is computed once per (instant, zone) rather than once per person.
let currentMonthMemo: { now: number; timeZone: string; key: string } | null = null;
function currentMonthKey(now: Date, timeZone?: string | null): string {
  const zone = timeZone ?? "";
  if (currentMonthMemo && currentMonthMemo.now === now.getTime() && currentMonthMemo.timeZone === zone) {
    return currentMonthMemo.key;
  }
  const key = monthKey(now, timeZone);
  currentMonthMemo = { now: now.getTime(), timeZone: zone, key };
  return key;
}

/**
 * Created in the same calendar month as `now`, on the organization's calendar when `timeZone` is
 * given, so every viewer of an organization sees the same "New this month" people. A timestamp
 * after the live instant `asOf` (a backfill or a skewed clock) is not yet a new person.
 */
export function isNewThisMonth(
  createdAt: string | null | undefined,
  now: Date,
  timeZone?: string | null,
  asOf: Date = new Date()
): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  // `now` anchors the month and can be hours old (it only ticks at organization midnight); the
  // upper bound is the live instant, so a person added since then still counts.
  if (Number.isNaN(created.getTime()) || created.getTime() > Math.max(now.getTime(), asOf.getTime())) return false;
  return monthKey(created, timeZone) === currentMonthKey(now, timeZone);
}

export type PeopleStatusCounts = {
  members: number;
  visitors: number;
  donors: number;
  vendors: number;
  prospects: number;
};

export type PeopleRecord = {
  id?: string;
  roles?: string[];
  status?: string | null;
  recentActivity?: boolean;
  name?: string;
  engagementNote?: string | null;
  email?: string | null;
  phone?: string | null;
  household?: string | null;
  companyName?: string | null;
  lastAttendance?: string;
  totalGiving?: number;
  photoUrl?: string;
  entityType?: "individual" | "company";
  lastEngagementDate?: string | null;
  /** ISO timestamp the record was created; drives the "new this month" filter. */
  createdAt?: string | null;
};

export type PeopleAggregation = {
  totalCount: number;
  roleCounts: PeopleRoleCounts;
  statusCounts: PeopleStatusCounts;
};

export function matchesPeopleFilter(
  person: PeopleRecord,
  options: PeopleFilterOptions
): boolean {
  const resolvedStatusOptions = normalizePeopleStatuses(options.statusOptions);
  const matchesFilter =
    options.activeFilter === "all" ||
    (options.activeFilter === "recent" && person.recentActivity) ||
    (options.activeFilter === "new" && isNewThisMonth(person.createdAt, options.now ?? new Date(), options.timeZone)) ||
    (options.activeFilter === "staff" && person.roles?.includes("staff")) ||
    (options.activeFilter === "volunteers" && person.roles?.includes("volunteer")) ||
    (options.activeFilter === "members" &&
      statusMatchesCategory(person.status, "member", resolvedStatusOptions)) ||
    (options.activeFilter === "visitors" &&
      statusMatchesCategory(person.status, "visitor", resolvedStatusOptions)) ||
    (options.activeFilter === "donors" &&
      statusMatchesCategory(person.status, "donor", resolvedStatusOptions)) ||
    (options.activeFilter === "vendors" &&
      statusMatchesCategory(person.status, "vendor", resolvedStatusOptions)) ||
    (options.activeFilter === "prospects" &&
      statusMatchesCategory(person.status, "prospect", resolvedStatusOptions));

  if (!matchesFilter) return false;

  const query = options.searchQuery?.trim().toLowerCase() ?? "";
  if (!query) return true;

  return (
    person.name?.toLowerCase().includes(query) ||
    person.engagementNote?.toLowerCase().includes(query) ||
    person.email?.toLowerCase().includes(query) ||
    person.household?.toLowerCase().includes(query) ||
    person.companyName?.toLowerCase().includes(query) ||
    false
  );
}

export function filterPeople(
  people: PeopleRecord[],
  options: PeopleFilterOptions
): PeopleRecord[] {
  return people.filter((person) => matchesPeopleFilter(person, options));
}

export function aggregatePeople(
  people: PeopleRecord[],
  statusOptions?: PeopleStatusOption[]
): PeopleAggregation {
  const resolvedStatusOptions = normalizePeopleStatuses(statusOptions);

  const roleCounts: PeopleRoleCounts = {
    staff: 0,
    volunteers: 0,
  };

  const statusCounts: PeopleStatusCounts = {
    members: 0,
    visitors: 0,
    donors: 0,
    vendors: 0,
    prospects: 0,
  };

  people.forEach((person) => {
    if (person.roles?.includes("staff")) {
      roleCounts.staff += 1;
    }
    if (person.roles?.includes("volunteer")) {
      roleCounts.volunteers += 1;
    }
    if (statusMatchesCategory(person.status, "member", resolvedStatusOptions)) {
      statusCounts.members += 1;
    }
    if (statusMatchesCategory(person.status, "visitor", resolvedStatusOptions)) {
      statusCounts.visitors += 1;
    }
    if (statusMatchesCategory(person.status, "donor", resolvedStatusOptions)) {
      statusCounts.donors += 1;
    }
    if (statusMatchesCategory(person.status, "vendor", resolvedStatusOptions)) {
      statusCounts.vendors += 1;
    }
    if (statusMatchesCategory(person.status, "prospect", resolvedStatusOptions)) {
      statusCounts.prospects += 1;
    }
  });

  return {
    totalCount: people.length,
    roleCounts,
    statusCounts,
  };
}

export function getPeopleFilterCounts(
  people: PeopleRecord[],
  statusOptions?: PeopleStatusOption[],
  now: Date = new Date(),
  timeZone?: string | null
): PeopleFilterCounts {
  const resolvedStatusOptions = normalizePeopleStatuses(statusOptions);
  const counts: PeopleFilterCounts = {
    all: people.length,
    recent: 0,
    new: 0,
    staff: 0,
    volunteers: 0,
    members: 0,
    visitors: 0,
    donors: 0,
    vendors: 0,
    prospects: 0,
  };

  people.forEach((person) => {
    if (person.recentActivity) counts.recent += 1;
    if (isNewThisMonth(person.createdAt, now, timeZone)) counts.new += 1;
    if (person.roles?.includes("staff")) counts.staff += 1;
    if (person.roles?.includes("volunteer")) counts.volunteers += 1;
    if (statusMatchesCategory(person.status, "member", resolvedStatusOptions)) counts.members += 1;
    if (statusMatchesCategory(person.status, "visitor", resolvedStatusOptions)) counts.visitors += 1;
    if (statusMatchesCategory(person.status, "donor", resolvedStatusOptions)) counts.donors += 1;
    if (statusMatchesCategory(person.status, "vendor", resolvedStatusOptions)) counts.vendors += 1;
    if (statusMatchesCategory(person.status, "prospect", resolvedStatusOptions)) counts.prospects += 1;
  });

  return counts;
}
